<template>
  <BaseFormLayout
    :title="
      editMode
        ? `Edit ${tripPlanningModule.planningTemplate}`
        : `Add ${tripPlanningModule.planningTemplate}`
    "
    :nonFieldErrors="formErrors.non_field_errors"
  >
    <template #actions>
      <SecondaryButton
        id="planningTemplateReset"
        v-if="!editMode"
        @click="resetForm()"
        btnClass="mr-2 text-uppercase rounded-lg"
        name="Reset"
      >
      </SecondaryButton>
      <PrimaryButton
        id="planningTemplateSubmit"
        @click="submitForm()"
        :disabled="!isValid"
        btnClass="text-uppercase rounded-lg"
        name="Submit"
      >
      </PrimaryButton>
    </template>
    <template #content>
      <v-form
        v-model="isValid"
        ref="tripPlanningTemplateForm"
        :key="formCount"
        @submit.prevent="submitForm"
      >
        <v-row>
          <v-col cols="12">
            <v-card flat class="rounded-lg pa-4">
              <v-row class="ma-0">
                <v-col cols="12">
                  <span class="text-subtitle-1 font-weight-bold">
                    {{ tripPlanningModule.template }} Information
                  </span>
                </v-col>
                <v-col cols="4" lg="4">
                  <InputField
                    id="name"
                    :label="`${tripPlanningModule.template} name`"
                    name="name"
                    :required="true"
                    v-model="tripPlanningTemplateForm.name"
                    :error-messages="formErrors.name"
                    @input="delete formErrors.name"
                  />
                </v-col>
                <v-col cols="4" lg="4">
                  <InputField
                    id="fill_ratio"
                    type="number"
                    max="100"
                    name="fill_ratio"
                    label="Fill ratio"
                    :required="true"
                    :rules="[
                      (val) =>
                        val <= 100 || 'Fill ratio can not be more than 100',
                    ]"
                    v-model="tripPlanningTemplateForm.fill_ratio"
                    :error-messages="formErrors.fill_ratio"
                    @input="delete formErrors.fill_ratio"
                  />
                </v-col>
                <v-col cols="4" lg="4">
                  <SelectField
                    id="configuration"
                    :label="`${configurationModule.configuration} *`"
                    :itemsList="configurationList"
                    name="configuration"
                    itemText="display_value"
                    itemValue="value"
                    :rules="[(val) => !!val || 'Configuration is required']"
                    v-model="tripPlanningTemplateForm.configuration"
                    :error-messages="formErrors.configuration"
                    @input="delete formErrors.configuration"
                  />
                </v-col>
                <v-col cols="4" lg="4">
                  <SelectField
                    id="warehouse"
                    :itemsList="warehouseList"
                    item-text="name"
                    item-value="id"
                    :required="tripPlanningTemplateForm.round_trip"
                    :label="
                      !tripPlanningTemplateForm.round_trip
                        ? 'Select Warehouse as Starting Location'
                        : 'Select Warehouse as Starting Location*'
                    "
                    :rules="
                      tripPlanningTemplateForm.round_trip
                        ? [
                            (val) =>
                              !!val ||
                              'Select Warehouse as Starting Location is required',
                          ]
                        : []
                    "
                    v-model="tripPlanningTemplateForm.start_location_warehouse"
                    :error-messages="formErrors.start_location_warehouse"
                    @input="delete formErrors.start_location_warehouse"
                  />
                </v-col>
                <!-- <v-col cols="4" lg="4">
                  <InputField
                    id="loading_time"
                    type="number"
                    label="Loading Time"
                    :required="false"
                    name="loading_time"
                    v-model="tripPlanningTemplateForm.loading_time"
                    :error-messages="formErrors.loading_time"
                    @input="delete formErrors.loading_time"
                  />
                </v-col>
                <v-col cols="4" lg="4">
                  <InputField
                    id="offloading_time"
                    type="number"
                    label="Offloading Time"
                    :required="false"
                    name="offloading_time"
                    v-model="tripPlanningTemplateForm.offloading_time"
                    :error-messages="formErrors.offloading_time"
                    @input="delete formErrors.offloading_time"
                  />
                </v-col> -->
              </v-row>
            </v-card>
          </v-col>
          <v-col cols="12">
            <v-card elevation="0" class="rounded-lg pa-5 h-100">
              <v-row>
                <v-col cols="12">
                  <span class="text-subtitle-1 font-weight-bold">
                    Constraints
                  </span>
                </v-col>
                <v-col cols="12" md="4">
                  <SwitchField
                    id="round_trip"
                    :label="`Round ${tripModule.trip}`"
                    name="round_trip"
                    v-model="tripPlanningTemplateForm.round_trip"
                    :error-messages="formErrors.round_trip"
                    @input="delete formErrors.round_trip"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <SwitchField
                    id="zone_constraint"
                    label="Zone Constraint"
                    name="zone_constraint"
                    v-model="tripPlanningTemplateForm.zone_constraint"
                    :error-messages="formErrors.zone_constraint"
                    @input="delete formErrors.zone_constraint"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <SwitchField
                    id="region_restriction"
                    :label="`${tripPlanningModule.regionRestriction}`"
                    name="region_restriction"
                    v-model="tripPlanningTemplateForm.region_restriction"
                    :error-messages="formErrors.region_restriction"
                    @input="delete formErrors.region_restriction"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <SwitchField
                    id="tag_validations"
                    :label="`${tripPlanningModule.tag} Validation`"
                    name="tag_validations"
                    v-model="tripPlanningTemplateForm.tag_validations"
                    :error-messages="formErrors.tag_validations"
                    @input="delete formErrors.tag_validations"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <SwitchField
                    id="disable_time_windows"
                    label="Disable Time Windows"
                    name="disable_time_windows"
                    v-model="tripPlanningTemplateForm.disable_time_windows"
                    :error-messages="formErrors.disable_time_windows"
                    @input="delete formErrors.disable_time_windows"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <SwitchField
                    id="traffic_jams"
                    label="Traffic Jams"
                    name="traffic_jams"
                    v-model="tripPlanningTemplateForm.traffic_jams"
                    :error-messages="formErrors.traffic_jams"
                    @input="delete formErrors.traffic_jams"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <SwitchField
                    id="toll_roads"
                    label="Toll Roads"
                    name="toll_roads"
                    v-model="tripPlanningTemplateForm.toll_roads"
                    :error-messages="formErrors.toll_roads"
                    @input="delete formErrors.toll_roads"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <SwitchField
                    id="can_driver_take_break"
                    label="Can driver take break"
                    name="can_driver_take_break"
                    v-model="tripPlanningTemplateForm.can_driver_take_break"
                    :error-messages="formErrors.can_driver_take_break"
                    @input="delete formErrors.can_driver_take_break"
                  />
                </v-col>
              </v-row>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card flat class="rounded-lg mt-4">
              <v-row class="ma-0">
                <v-col cols="12">
                  <span class="text-subtitle-1 font-weight-bold">
                    Advanced Configuration
                  </span>
                </v-col>
                <v-col cols="12" md="4">
                  <input-field
                    type="number"
                    id="max_stop_location"
                    label="Max Stop Location"
                    name="max_stop_location"
                    :rules="[
                      (val) =>
                        !val ||
                        val >= 2 ||
                        'max stop location must be more than 2',
                    ]"
                    v-model="tripPlanningTemplateForm.max_stop_location"
                    :error-messages="formErrors.max_stop_location"
                  >
                  </input-field>
                </v-col>
                <v-col cols="12" md="4">
                  <input-field
                    type="number"
                    id="transport_speed_multiplier"
                    label="Transport Speed Multiplier"
                    name="transport_speed_multiplier"
                    :rules="[
                      (val) =>
                        !val ||
                        (val >= 0.1 && val <= 3) ||
                        'Value should be between 0.1 and 3',
                    ]"
                    v-model="
                      tripPlanningTemplateForm.transport_speed_multiplier
                    "
                    :error-messages="formErrors.transport_speed_multiplier"
                    @input="delete formErrors.transport_speed_multiplier"
                  >
                  </input-field>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </v-form>
    </template>
  </BaseFormLayout>
</template>

<script>
import { bus } from "@/main.js";
import BaseFormLayout from "@/components/LayoutComponents/BaseFormLayout.vue";
import {
  getChoicesFromOptionsRequest,
  returnToday,
  getEmptyObjectKeyRemove,
} from "@/utils/functions";
import {
  tripModule,
  tripPlanningModule,
  configurationModule,
} from "@/utils/moduleMapping";
import InputField from "@/components/ComponentLibrary/FormInput/InputField.vue";

export default {
  components: {
    BaseFormLayout,
    InputField,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    editMode: {
      required: true,
    },
  },
  data() {
    return {
      returnToday,
      configurationModule,
      tripPlanningModule,
      tripModule,
      fileInput: null,
      formCount: 0,
      isValid: true,

      formErrors: {},
      tripPlanningTemplateForm: {
        round_trip: false,
        zone_constraint: false,
        region_restriction: false,
        tag_validations: false,
        disable_time_windows: false,
        traffic_jams: false,
        toll_roads: false,
      },
      configurationList: [],
      warehouseList: [],

      advancedConfiguration: false,
    };
  },
  computed: {
    selectedBranch() {
      let obj = this.$helper.getUserDetailsLocalStorage();
      if (obj) {
        return obj.preferred_branch.id;
      } else {
        return null;
      }
    },
  },
  methods: {
    resetForm() {
      this.$refs.tripPlanningTemplateForm.reset();
      this.tripPlanningTemplateForm = {
        round_trip: false,
        zone_constraint: false,
        region_restriction: false,
        tag_validations: false,
        disable_time_windows: false,
        traffic_jams: false,
        toll_roads: false,
      };
    },
    submitForm() {
      if (!this.$refs.tripPlanningTemplateForm.validate()) {
        bus.$emit("showToastMessage", {
          message: "Please correct all the errors to submit the form!",
          color: "error",
        });
        return;
      }
      bus.$emit("showLoader", true);
      if (!this.editMode) {
        let payload = {
          ...getEmptyObjectKeyRemove(this.tripPlanningTemplateForm),
          branch: this.selectedBranch,
        };
        this.$api.tripPlanningTemplate
          .createTripPlanningTemplateObject(payload)
          .then(() => {
            this.$router.back();
            bus.$emit("showToastMessage", {
              message: "Template Created Successfully!",
              color: "success",
            });
            bus.$emit("showLoader", false);
          })
          .catch((err) => {
            console.error(err);
            this.formErrors = err.data;
            bus.$emit("showLoader", false);
          });
      } else {
        this.$api.tripPlanningTemplate
          .updateTripPlanningTemplateObject(
            this.tripPlanningTemplateForm.id,
            getEmptyObjectKeyRemove(this.tripPlanningTemplateForm)
          )
          .then(() => {
            this.$router.back();
            bus.$emit("showLoader", false);
          })
          .catch((err) => {
            console.error(err);
            this.formErrors = err.data;
            bus.$emit("showLoader", false);
          });
      }
    },
    getTripPlanningTemplateObject(id) {
      bus.$emit("showLoader", true);
      this.$api.tripPlanningTemplate
        .getTripPlanningTemplateObject(id)
        .then((res) => {
          this.tripPlanningTemplateForm = res.data;
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
    getTripPlanningTemplateOptions() {
      bus.$emit("showLoader", true);
      this.$api.tripPlanningTemplate
        .getTripPlanningTemplateOptions({ request_type: "form" })
        .then((res) => {
          this.configurationList = res?.data?.configuration;
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
    getWarehouseList(params = {}) {
      params = {
        ...params,
        limit: "all",
      };
      return this.$api.warehouse
        .getWarehouseSelectList(params)
        .then((res) => {
          this.warehouseList = res.data;
        })
        .catch((err) => {
          console.log(err);
        });
    },
  },
  mounted() {
    this.getTripPlanningTemplateOptions();
    this.getWarehouseList();
    if (
      this.$route.params &&
      this.$route.params.id &&
      !isNaN(this.$route.params.id)
    ) {
      this.getTripPlanningTemplateObject(this.$route.params.id);
    }
  },
};
</script>

<style>
.v-select__slot label {
  z-index: 2;
}
</style>
